import { useRef, useState } from "react";
import { IconeLixeira, IconeOlho } from "../icons";
import { normalizarTextoPtBr } from "../../utils/textoPtBr";
import { ACEITA_IMAGEM_PNG_JPG, prepararImagemParaUpload, tipoImagemValido } from "../../utils/imagemUpload";
import { mostrarToastErro } from "../../utils/toast";
import { BotaoContorno } from "./BotaoAcao";
import { BotaoIcone } from "./BotaoIcone";
import { ModalConfirmacao } from "./ModalConfirmacao";
import { ModalAjusteImagem } from "./ModalAjusteImagem";
import styles from "./UploadImagem.module.css";

interface UploadImagemProps {
  url?: string;
  nome?: string;
  desabilitado?: boolean;
  aviso?: string;
  carregando?: boolean;
  compacto?: boolean;
  /** Coloca a exclusão como ícone dentro do canto superior da área de upload. */
  removerInterno?: boolean;
  /**
   * Abre o ajuste circular (arrastar + zoom) antes de entregar a imagem —
   * usado nos avatares, onde o recorte precisa caber no círculo.
   */
  ajusteCircular?: boolean;
  onSelecionar: (dataUrl: string, arquivo: File) => void | Promise<void>;
  onRemover?: () => void | Promise<void>;
  onVisualizar?: () => void;
  tituloVisualizar?: string;
}

const IconeCamera = () => (
  <svg viewBox="0 0 24 24" width="30" height="30" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 8a2 2 0 0 1 2-2h2l1.5-2h5L18 6h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8z" />
    <circle cx="13" cy="13" r="3.4" />
  </svg>
);

/** Upload de imagem em DROPZONE (arrastar/soltar ou clicar), no padrao do legado. */
export function UploadImagem({
  url,
  nome,
  desabilitado = false,
  aviso,
  carregando = false,
  compacto = false,
  removerInterno = false,
  ajusteCircular = false,
  onSelecionar,
  onRemover,
  onVisualizar,
  tituloVisualizar = "Visualizar imagem",
}: UploadImagemProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [arrastando, setArrastando] = useState(false);
  const [confirmandoRemocao, setConfirmandoRemocao] = useState(false);
  const [ajuste, setAjuste] = useState<{ dataUrl: string; arquivo: File } | null>(null);
  const [removendo, setRemovendo] = useState(false);
  const avisoNormalizado = aviso ? normalizarTextoPtBr(aviso) : undefined;

  async function processar(arquivo?: File | null) {
    if (!arquivo || desabilitado) return;
    if (!tipoImagemValido(arquivo)) {
      mostrarToastErro("Selecione uma imagem PNG ou JPG.");
      return;
    }
    try {
      const dataUrl = await prepararImagemParaUpload(arquivo);
      if (ajusteCircular) {
        setAjuste({ dataUrl, arquivo });
        return;
      }
      await onSelecionar(dataUrl, arquivo);
    } catch (erro) {
      mostrarToastErro(erro instanceof Error ? erro.message : "Erro ao processar a imagem.");
    }
  }

  async function confirmarRemocao() {
    if (!onRemover) return;
    setRemovendo(true);
    try {
      await onRemover();
      setConfirmandoRemocao(false);
    } finally {
      setRemovendo(false);
    }
  }

  return (
    <>
      <div
        className={styles.wrap}
        data-campo-modal="true"
        data-campo-tamanho="total"
        data-campo-progresso={desabilitado && !url ? "false" : "true"}
        data-campo-preenchido={url ? "true" : "false"}
      >
        <div
          className={`${styles.dropzone} ${compacto ? styles.dropzoneCompacta : ""} ${arrastando ? styles.arrastando : ""} ${desabilitado ? styles.desabilitado : ""}`}
          onClick={() => !desabilitado && inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            if (!desabilitado) setArrastando(true);
          }}
          onDragLeave={() => setArrastando(false)}
          onDrop={(e) => {
            e.preventDefault();
            setArrastando(false);
            void processar(e.dataTransfer.files?.[0]);
          }}
          role="button"
          tabIndex={0}
        >
          <input ref={inputRef} type="file" accept={ACEITA_IMAGEM_PNG_JPG} hidden onChange={(e) => void processar(e.target.files?.[0])} />

          {onVisualizar && (
            <BotaoContorno
              somenteIcone
              className={`${styles.visualizarBtn} tooltip`}
              aria-label={tituloVisualizar}
              data-tooltip={tituloVisualizar}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!url || desabilitado) return;
                onVisualizar();
              }}
              disabled={!url || desabilitado}
              iconeInicio={<IconeOlho />}
            />
          )}

          {onRemover && removerInterno && url && (
            <BotaoIcone
              titulo="Remover imagem"
              pequeno
              perigo
              className={styles.removerInternoBtn}
              disabled={desabilitado || removendo}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setConfirmandoRemocao(true);
              }}
            >
              <IconeLixeira />
            </BotaoIcone>
          )}

          {url ? (
            <img className={`${styles.preview} ${compacto ? styles.previewCompacta : ""}`} src={url} alt={nome || "Imagem"} />
          ) : (
            <span className={`${styles.icone} ${compacto ? styles.iconeCompacto : ""}`}><IconeCamera /></span>
          )}

          <p className={`${styles.texto} ${compacto ? styles.textoCompacto : ""}`}>
            {carregando
              ? "Enviando..."
              : compacto
                ? <span className={styles.link}>Escolher imagem</span>
                : <>Arraste e solte a imagem aqui ou <span className={styles.link}>escolha a imagem</span></>
            }
          </p>
          <p className={`${styles.status} ${compacto ? styles.statusCompacto : ""}`}>
            {avisoNormalizado ? avisoNormalizado : url ? "Imagem salva" : "Nenhuma imagem selecionada"}
          </p>
        </div>

        {onRemover && !removerInterno && (
          <footer className={`${styles.rodape} ${compacto ? styles.rodapeCompacto : ""}`}>
            <BotaoContorno
              disabled={!url || desabilitado || removendo}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setConfirmandoRemocao(true);
              }}
            >
              {compacto ? "Remover" : "Remover imagem"}
            </BotaoContorno>
          </footer>
        )}
      </div>

      <ModalConfirmacao
        aberto={confirmandoRemocao}
        titulo="Excluir imagem"
        mensagem="Tem certeza que deseja remover esta imagem? Esta ação não pode ser desfeita."
        textoConfirmar="Excluir"
        perigo
        processando={removendo}
        onConfirmar={() => void confirmarRemocao()}
        onCancelar={() => !removendo && setConfirmandoRemocao(false)}
      />

      {/* Enquadramento no círculo do avatar antes de entregar a imagem. */}
      {ajuste ? (
        <ModalAjusteImagem
          aberto
          imagem={ajuste.dataUrl}
          onCancelar={() => setAjuste(null)}
          onConfirmar={(recortada) => {
            const arquivo = ajuste.arquivo;
            setAjuste(null);
            void onSelecionar(recortada, arquivo);
          }}
        />
      ) : null}
    </>
  );
}
